import { useState, useEffect, useRef } from 'react'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui'
import { InputOTP } from '../ui/input-otp'
import { createPasskeyClient, passkeyError, type PasskeyAction } from '../../auth/passkeys'

export interface ActionMfaProof {
  mfaCode?: string
  passkeyApproval?: string
}

interface ApproveMfaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (proof: ActionMfaProof) => Promise<void>
  isSubmitting?: boolean
  title?: string
  apiUrl?: string
  token?: string
  action?: PasskeyAction
  admin?: boolean
  targetKey?: string
}

export function ApproveMfaDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
  title = 'Approve Address',
  apiUrl,
  token,
  action,
  admin = false,
  targetKey,
}: ApproveMfaDialogProps) {
  const [mfaCode, setMfaCode] = useState('')
  const [passkeyErrorMessage, setPasskeyErrorMessage] = useState('')
  const [passkeySubmitting, setPasskeySubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const attempt = useRef(0)

  const invalidateAttempt = () => {
    attempt.current += 1
    isSubmittingRef.current = false
  }

  // Reset code when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setMfaCode('')
      setPasskeyErrorMessage('')
      setPasskeySubmitting(false)
      invalidateAttempt()
    }
  }, [open])

  useEffect(() => {
    if (open) {
      setMfaCode('')
      setPasskeyErrorMessage('')
      setPasskeySubmitting(false)
      invalidateAttempt()
    }
  }, [action, admin, open, targetKey, token])

  useEffect(() => {
    return () => {
      attempt.current += 1
      isSubmittingRef.current = false
    }
  }, [])

  const [passkeysEnabled, setPasskeysEnabled] = useState(false)
  useEffect(() => {
    let active = true
    if (!open || !apiUrl || !action) {
      setPasskeysEnabled(false)
      return () => {
        active = false
      }
    }
    void createPasskeyClient(apiUrl, admin)
      .enabled()
      .then(enabled => {
        if (active) setPasskeysEnabled(enabled)
      })
      .catch(() => {
        if (active) setPasskeysEnabled(false)
      })
    return () => {
      active = false
    }
  }, [action, admin, apiUrl, open])

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (mfaCode.length === 6 && !isSubmitting && !isSubmittingRef.current) {
      const currentAttempt = ++attempt.current
      isSubmittingRef.current = true
      void onConfirm({ mfaCode }).finally(() => {
        if (currentAttempt !== attempt.current) return
        isSubmittingRef.current = false
        setMfaCode('')
      })
    }
  }, [mfaCode, onConfirm, isSubmitting])

  const verifyPasskey = async () => {
    if (!apiUrl || !token || !action || isSubmitting || isSubmittingRef.current) return
    const currentAttempt = ++attempt.current
    isSubmittingRef.current = true
    setPasskeySubmitting(true)
    setPasskeyErrorMessage('')
    try {
      const passkeyApproval = await createPasskeyClient(apiUrl, admin).action(token, action)
      if (currentAttempt !== attempt.current) return
      await onConfirm({ passkeyApproval })
    } catch (error) {
      if (currentAttempt === attempt.current) setPasskeyErrorMessage(passkeyError(error))
    } finally {
      if (currentAttempt === attempt.current) {
        setPasskeySubmitting(false)
        isSubmittingRef.current = false
      }
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) invalidateAttempt()
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center py-4">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Multi-Factor Authentication</h2>
          <p className="text-gray-600 mb-6 text-xs text-center">
            To confirm please enter authenticator certification code
          </p>
          <InputOTP
            value={mfaCode}
            onChange={setMfaCode}
            length={6}
            autoFocus={true}
            disabled={isSubmitting || passkeySubmitting}
            className="text-center text-2xl font-bold h-[53px] w-[47.5px]"
            name="approve-address-mfa-code"
            data-testid="approve-address-mfa-otp-input"
          />
          {passkeysEnabled && action && apiUrl && token && (
            <Button
              type="button"
              className="mt-5"
              disabled={isSubmitting || passkeySubmitting}
              onClick={() => void verifyPasskey()}
            >
              {passkeySubmitting ? 'Verifying passkey…' : 'Verify with a passkey'}
            </Button>
          )}
          {passkeyErrorMessage && (
            <p className="mt-3 text-center text-xs text-destructive" role="alert">
              {passkeyErrorMessage}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
